export const MAX_AVATAR_BYTES = 1024 * 1024;
export const AVATAR_SIDE = 256;

export type ResizedProfilePhoto = {
  blob: Blob;
  contentType: "image/jpeg" | "image/png";
  previewUrl: string;
  originalBytes: number;
  outputBytes: number;
};

function loadImage(file: File) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("This image could not be opened."));
    };
    image.src = url;
  });
}

function canvasBlob(canvas: HTMLCanvasElement, contentType: string, quality?: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("This image could not be resized.")), contentType, quality);
  });
}

export async function resizeProfilePhoto(file: File): Promise<ResizedProfilePhoto> {
  if (file.type !== "image/jpeg" && file.type !== "image/png") {
    throw new Error("Choose a JPEG or PNG image.");
  }

  const image = await loadImage(file);
  const sourceSide = Math.min(image.naturalWidth, image.naturalHeight);
  if (!sourceSide) throw new Error("This image has no readable dimensions.");

  const canvas = document.createElement("canvas");
  canvas.width = AVATAR_SIDE;
  canvas.height = AVATAR_SIDE;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Image resizing is unavailable in this browser.");

  const sourceX = (image.naturalWidth - sourceSide) / 2;
  const sourceY = (image.naturalHeight - sourceSide) / 2;
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(image, sourceX, sourceY, sourceSide, sourceSide, 0, 0, AVATAR_SIDE, AVATAR_SIDE);

  let contentType: "image/jpeg" | "image/png" = file.type === "image/png" ? "image/png" : "image/jpeg";
  let blob = await canvasBlob(canvas, contentType, contentType === "image/jpeg" ? 0.9 : undefined);

  if (blob.size > MAX_AVATAR_BYTES) {
    contentType = "image/jpeg";
    for (const quality of [0.86, 0.76, 0.66, 0.56]) {
      blob = await canvasBlob(canvas, contentType, quality);
      if (blob.size <= MAX_AVATAR_BYTES) break;
    }
  }

  if (blob.size > MAX_AVATAR_BYTES) {
    throw new Error("This photo could not be reduced below 1 MB. Please choose another image.");
  }

  return {
    blob,
    contentType,
    previewUrl: URL.createObjectURL(blob),
    originalBytes: file.size,
    outputBytes: blob.size,
  };
}
